import { CATEGORY_KEY_TO_OFFICIAL, toCategoryKeyForApi } from '@/utils/edtechCategoryNames';

/**
 * Tenant-scoped competitor definitions for Procurement Competitor Intelligence.
 * Competitors = other players in the same category as the tenant.
 * Uses: tenant → categories → competitor vendor names (from SegmentBuilder tech stack).
 */

// Category → vendor names (from SegmentBuilder techStackCategories, excluding In-house/None)
const CATEGORY_VENDORS = {
  LMS: ['Canvas', 'Google Classroom', 'Blackboard', 'Moodle', 'D2L', 'Open LMS', 'Sakai', 'Schoology'],
  SIS: ['Ellucian Banner', 'Ellucian Colleague', 'Paradigm', 'Oracle PeopleSoft', 'Workday Student', 'PowerSchool', 'TechnologyOne', 'Student One', 'Adapt IT', 'Veracross'],
  CRM: ['Salesforce', 'Microsoft Dynamics 365', 'Anthology CRM', 'Oracle', 'SugarCRM', 'Tribal', 'Callista', 'Paradigm', 'StudyLink Connect', 'Recruitment CRM'],
  Assessment: ['ALEK'],
  Authoring: ['Storyline', 'Articulate'],
  Immersive: ['HoloLens', 'Intracon', 'Meta', 'Strivr'],
  EHR: ['DxCare', 'Easily', 'aXigate', 'Care4U'],
  iPass: ['Enovacom', 'InterSystems', 'Lifen'],
  'Patient Portal': ['Doctolib', 'ViaPatient'],
  Admissions: ['OpenApply'],
  'School-to-Home': ['Schoolsbuddy'],
  Tower: ['Kinaxis', 'SAP'],
  Proctoring: ['Proctorio', 'Respondus', 'ProctorU', 'Honorlock', 'Inspera', 'Zoom', 'The Invigilator', 'Smowl', 'SMOWL'],
  'Academic Integrity': ['Turnitin', 'Compilatio', 'Plagius', 'iThenticate', 'Clarivate', 'Grammarly', 'Copyleaks', 'Ouriginal', 'Urkund'],
  ePortfolio: ['PebblePad', 'Mahara', 'Portfolium', 'Brightspace', 'Blackboard', 'Moodle', 'Sakai'],
  Library: ['Ex Libris Alma', 'Overdrive', 'OCLC WorldShare', 'SirsiDynix', 'Koha', 'EBSCO', 'Archimed Syracuse', 'Britannica', 'Kognity'],
  'Course Evaluation': ['Qualtrics', 'Evasys', 'Explorance', 'Watermark', 'Anthology', 'SmartEvals', 'CollegeNet', 'LimeSurvey', 'Snap Surveys', 'SmartSurvey', 'FeedbackFruits', 'Berger-Levrault'],
};

// Internal category codes → category_key (for edtechCategoryNames) or fallback official name
const INTERNAL_TO_OFFICIAL = {
  LMS: CATEGORY_KEY_TO_OFFICIAL.lms,
  Proctoring: CATEGORY_KEY_TO_OFFICIAL.proctoring,
  Assessment: CATEGORY_KEY_TO_OFFICIAL.online_exams,
  SIS: CATEGORY_KEY_TO_OFFICIAL.sis,
  ePortfolio: 'e-Portfolio / Comprehensive Learner Record',
  Library: 'Library & Discovery Services / Reading List Systems',
  Immersive: CATEGORY_KEY_TO_OFFICIAL.xr_immersive,
  Authoring: CATEGORY_KEY_TO_OFFICIAL.elearning_authoring_services,
  Tower: CATEGORY_KEY_TO_OFFICIAL.talent_management_hcm,
  EHR: 'EHR / Clinical Systems',
  iPass: CATEGORY_KEY_TO_OFFICIAL.integration_data,
  'Patient Portal': 'Patient Portal',
  Admissions: CATEGORY_KEY_TO_OFFICIAL.admissions_crm,
  'School-to-Home': 'Student Case Management & Retention CRM',
  'Course Evaluation': CATEGORY_KEY_TO_OFFICIAL.course_evaluation,
  course_evaluation: CATEGORY_KEY_TO_OFFICIAL.course_evaluation,
  academic_integrity: CATEGORY_KEY_TO_OFFICIAL.academic_integrity,
  'Academic Integrity': CATEGORY_KEY_TO_OFFICIAL.academic_integrity,
  student_success: CATEGORY_KEY_TO_OFFICIAL.student_success,
  timetabling: CATEGORY_KEY_TO_OFFICIAL.timetabling,
  admissions_crm: CATEGORY_KEY_TO_OFFICIAL.admissions_crm,
  finance_erp: CATEGORY_KEY_TO_OFFICIAL.finance_erp,
  proctoring: CATEGORY_KEY_TO_OFFICIAL.proctoring,
  marking: CATEGORY_KEY_TO_OFFICIAL.marking,
  language_learning: CATEGORY_KEY_TO_OFFICIAL.language_learning,
  it_hardware: CATEGORY_KEY_TO_OFFICIAL.it_hardware,
};

// Tenant → categories they operate in (aligned with MarketShare.jsx)
const TENANT_CATEGORIES = {
  // [2026-10-04] instructure is LMS ONLY (Tiago). 'sis' and 'eportfolio' were dropped: the
  // eportfolio key never reached a filter anyway — it has no official-label twin here, so the
  // categoryOptions narrowing in Procurement.jsx discarded it silently. NOTE this entry doubles as
  // the fallback for an unmapped tenant (see getDealCategoriesForTenant), so a NEW tenant with no
  // entry now defaults to LMS only; no current tenant relies on it — all have their own entry.
  instructure: ['lms', 'Learning Management Systems (LMS)'],
  smowl: ['Remote Proctoring', 'Online Exam Platforms'],
  surpass: ['online_exams', 'Online Exam Platforms', 'Remote Proctoring'],
  surpass_v2: ['online_exams', 'Online Exam Platforms', 'Remote Proctoring'],
  vretta: ['online_exams', 'Online Exam Platforms'],
  diambra: ['lms', 'Learning Management Systems (LMS)', 'Remote Proctoring'],
  demarque: ['library', 'Library & Discovery Services / Reading List Systems', 'Digital Course Content & Inclusive Access'],
  demarque_v2: ['library', 'Library & Discovery Services / Reading List Systems', 'Digital Course Content & Inclusive Access'],
  bibliotheca: ['library_self_service', 'Library Self-Service, RFID & Automated Handling'],
  skillwell: ['lms', 'Learning Management Systems (LMS)', 'XR / Immersive Learning Platforms', 'Course Authoring Platform', 'E-learning Content / Authoring / Learning Services'],
  kairadigital: ['Tower', 'sis'],
  wayground: ['lms', 'online_exams', 'Online Exam Platforms'],
  plataformaa: ['lms', 'online_exams', 'Online Exam Platforms', 'XR / Immersive Learning Platforms'],
  calmedica: ['Telemedicine', 'Patient Engagement & Remote Monitoring'],
  faria: ['lms', 'sis', 'Admissions', 'School-to-Home'],
  edqwest: ['lms', 'sis'],
  clairelabs: ['lms', 'sis'],
  prowise: ['lms', 'Learning Management Systems (LMS)', 'sis', 'Student Information Systems (SIS)'],
  sebit: ['lms', 'sis'],
  base44: ['lms', 'Learning Management Systems (LMS)', 'sis', 'Student Information Systems (SIS)', 'eportfolio'],
  evasys: ['course_evaluation', 'Course & Teaching Evaluation', 'online_exams', 'Online Exam Platforms'],
  evasys_v2: ['course_evaluation', 'Course & Teaching Evaluation', 'online_exams', 'Online Exam Platforms'],
  synap: ['course_evaluation', 'Course & Teaching Evaluation', 'online_exams', 'Online Exam Platforms'],
  /* [2026-10-20] Aligned to TENANT_DEFAULT_CATEGORIES.scantron in the BE, which Tiago narrowed to
   * OMR + the two evasys_V2 categories. Two FE-only defects fixed while here:
   *   · 'Course & Teaching Evaluation' matched 0 rows in BOTH deals and closed_deals — the real
   *     display name for course_evaluation is 'Feedback & Evaluation' (taxonomy.ts).
   *   · the 'omr_document_scanning' KEY was missing, so only the comma-free label carried it. */
  scantron: [
    'online_exams', 'Online Exam Platforms',
    'course_evaluation', 'Feedback & Evaluation',
    'omr_document_scanning', 'OMR & Document Scanning Hardware',
  ],
  learnwise: ['AI & Virtual Assistants', 'Marking / Grading Platforms'],
  funidata: ['sis', 'Student Information Systems (SIS)', 'Faculty Information & Workload Systems'],
  akari: ['Curriculum Enrichment & Curriculum Management Platforms'],
  gravyty: ['Alumni & Advancement Systems', 'Student Success & Engagement'],
  uplanner: ['student_success', 'timetabling'],
  edusign: ['timetabling', 'course_evaluation', 'student_success'],
  liaison: ['admissions_crm'],
  fullfabric: ['admissions_crm', 'Student Recruitment & Engagement CRM', 'alumni_advancement', 'Alumni & Advancement Systems'],
  fullfabric_v2: ['admissions_crm', 'Student Recruitment & Engagement CRM', 'alumni_advancement', 'Alumni & Advancement Systems'],
  studentcrm: ['admissions_crm', 'Student Recruitment & Engagement CRM'],
  /* [2026-10-15] unit4 — ERP AND HCM, mirroring TENANT_DEFAULT_CATEGORIES.unit4 in
   * api/src/routes/procurementRoutes.ts (which already had both). This list had ERP only, so Contract
   * Intelligence opened with Finance & ERP selected and HCM missing (Tiago). */
  unit4: ['finance_erp', 'Finance & ERP', 'talent_management_hcm', 'Talent Management / HCM'],
  /* [2026-10-14] access_people — The Access Group's Access People division (PeopleXD HR &
   * payroll, plus Access Financials/Dimensions). BE lens profile 41,
   * tenant_market_profiles.categories = ['talent_management_hcm','finance_erp'].
   * 🔴 MUST MIRROR TENANT_DEFAULT_CATEGORIES.access_people in
   * api/src/routes/procurementRoutes.ts — when these drift, the exact-match narrowing in
   * Procurement.jsx silently empties preferredLiveCategories and My Deals renders "(0)"
   * with the tab still visible. Both spellings, because getDealCategoriesForTenant maps
   * through INTERNAL_TO_OFFICIAL and de-duplicates. */
  /* [2026-10-20] procurement_platform ADDED on Tiago's instruction ("access people: erp, hcm,
   * procurement"). 0 live deals / 19 closed, so expect nothing from it in Live Radar. The fit
   * profile also claimed LXP/LMS, Career Services, Student Payments, ECM/e-Signature and GRC —
   * not the tenant's lanes, deliberately absent. */
  access_people: ['talent_management_hcm', 'Talent Management / HCM',
                  'finance_erp', 'Finance & ERP',
                  'procurement_platform', 'Procurement & eSourcing Platforms'],
  /* [2026-10-15] niryo — Niryo, the French educational robot-arm maker. BE lens profile 42,
   * tenant_market_profiles.categories = ['robotics_automation_training'] (the category added to
   * api/src/config/taxonomy.ts on 2026-10-15 for this tenant).
   * 🔴 MUST MIRROR TENANT_DEFAULT_CATEGORIES.niryo in api/src/routes/procurementRoutes.ts — when
   * these drift, the exact-match narrowing in Procurement.jsx silently empties
   * preferredLiveCategories and My Deals renders "(0)" with the tab still visible.
   * 🔴 The LABEL carries the data: measured 2026-10-15, `closed_deals` holds 332 rows under
   * 'Robotics, Automation & Industry 4.0 Training' and ZERO under the slug, `deals` 13 (also
   * label). Both spellings anyway, because getDealCategoriesForTenant maps through
   * INTERNAL_TO_OFFICIAL and de-duplicates. */
  niryo: ['robotics_automation_training', 'Robotics, Automation & Industry 4.0 Training'],
  /* [2026-10-16] 4science — 4Science (DSpace-CRIS, DSpace, DSpace-GLAM). BE lens profile 43,
   * tenant_market_profiles.categories = research_information_cris, digital_repository, digital_preservation.
   * 🔴 MUST MIRROR TENANT_DEFAULT_CATEGORIES['4science'] in api/src/routes/procurementRoutes.ts.
   * Both spellings, because deals/closed_deals hold the LABEL. */
  /* [2026-10-20] 'Digital Preservation & Archival Systems' ADDED — the taxonomy's display name for
   * digital_preservation is '… & Archives', which NO row in deals/closed_deals holds. The real
   * spelling carries 21 live and 291 closed rows, so without it this third category reached nothing. */
  '4science': ['research_information_cris', 'Research Information Management (CRIS)',
               'digital_repository', 'Institutional & Digital Repositories',
               'digital_preservation', 'Digital Preservation & Archives',
               'Digital Preservation & Archival Systems'],
  /* [2026-10-20] dreamapply — DreamApply (international admissions / application management).
   * Width taken from its client_product_profiles row, i.e. the FIT engine's own scope, so My Deals
   * and the Fit column agree (Tiago, 2026-10-20). With no entry it fell through to the
   * `instructure` default and filtered on LMS + SIS + eportfolio.
   * 🔴 MUST MIRROR TENANT_DEFAULT_CATEGORIES.dreamapply in api/src/routes/procurementRoutes.ts.
   * Both spellings, because deals/closed_deals hold the LABEL (deals: Student Recruitment &
   * Engagement CRM 569, Study Abroad 10).
   * The profile was narrowed on 2026-10-20 and no longer lists SIS or Student Payments, so neither
   * is here. Widen both files together or the Fit column and the list disagree. */
  dreamapply: ['admissions_crm', 'Student Recruitment & Engagement CRM',
               'study_abroad_intl', 'Study Abroad & International Student Management'],
  /* [2026-10-22] blackbullion — Blackbullion (student financial wellbeing, hardship and
   * scholarship fund administration, retention risk). BE lens profile 46,
   * tenant_market_profiles.categories = ['student_success']. ONE category: Tiago, "student success
   * only". Both forms, because getDealCategoriesForTenant maps through INTERNAL_TO_OFFICIAL (which
   * already has student_success) and de-duplicates, while other call sites test the raw key.
   * 🔴 The LABEL is the one that reaches data: measured 2026-10-22, the slug 'student_success'
   * holds ZERO rows in `deals` and `closed_deals`; 'Student Success & Engagement' holds 426 and
   * 2,711. A slug-only entry renders My Deals "(0)" with the tab still visible.
   * 🔴 'Student Case Management & Retention CRM' is a SEPARATE taxonomy key and deliberately
   * excluded, per "student success only".
   * 🔴 [2026-10-23] SECOND CATEGORY ADDED — student_financial_wellbeing, split out of
   * student_success on 2026-10-22 (the same day "student success only" was said), and taxonomy.ts
   * states both are carried so nothing already categorised is stranded. It is the thin slice
   * Blackbullion actually sells. tenant_market_profiles.categories already reads
   * ['student_success','student_financial_wellbeing']; this brings the FE into line.
   * Measured before adding: the label 'Student Financial Wellbeing & Hardship Funding' held ZERO
   * deals until 17 were recategorised into it on 2026-10-23 — scholarship, bursary and hardship
   * administration previously filed under SIS, Alumni & Advancement and Student Payments.
   * 🔴 MUST MIRROR TENANT_DEFAULT_CATEGORIES.blackbullion in api/src/routes/procurementRoutes.ts. */
  blackbullion: ['student_success', 'Student Success & Engagement',
                 'student_financial_wellbeing', 'Student Financial Wellbeing & Hardship Funding'],
  /* [2026-10-31] wooclap — Wooclap (live polling / audience response). BE lens `polling`, ONE category.
   * Both forms: closed_deals hold the LABEL ('Classroom Engagement & Polling' 359 after 73 e-voting
   * awards moved to IT Services on 2026-10-31).
   * 🔴 MUST MIRROR TENANT_DEFAULT_CATEGORIES.wooclap in api/src/routes/procurementRoutes.ts. */
  wooclap: ['polling', 'Classroom Engagement & Polling'],
  /* [2026-11-02] polleverywhere — the same single polling category as wooclap.
   * 🔴 MUST MIRROR TENANT_DEFAULT_CATEGORIES.polleverywhere in api/src/routes/procurementRoutes.ts. */
  polleverywhere: ['polling', 'Classroom Engagement & Polling'],
  /* [2026-11-03] positivo — one lens, it_hardware, as jpik; both spellings (BE label + FE 'IT Hardware').
   * 🔴 MUST MIRROR TENANT_DEFAULT_CATEGORIES.positivo in api/src/routes/procurementRoutes.ts. */
  positivo: ['it_hardware', 'IT Hardware'],
  /* [2026-10-18] testwe — TestWe (online exams, ProctorWe, AI Assistant). BE lens profile 44,
   * tenant_market_profiles.categories = online_exams, proctoring, marking.
   * 🔴 MUST MIRROR TENANT_DEFAULT_CATEGORIES.testwe in api/src/routes/procurementRoutes.ts.
   * Both spellings, because deals/closed_deals hold the LABEL (deals: Online Exam Platforms 526,
   * Remote Proctoring 349, Marking / Grading Platforms 144). */
  testwe: ['online_exams', 'Online Exam Platforms',
           'proctoring', 'Remote Proctoring',
           'marking', 'Marking / Grading Platforms'],
  /* [2026-10-07] Widened from lms alone to the lens's three categories (BE profile 38 /
   * tenant_market_profiles.categories). Must mirror TENANT_DEFAULT_CATEGORIES.blackboard in
   * api/src/routes/procurementRoutes.ts — when these two drift, the exact-match narrowing in
   * Procurement.jsx silently empties preferredLiveCategories and My Deals renders "(0)" with the
   * tab still visible, which is how the jpik/it_hardware defect presented. Both spellings, because
   * getDealCategoriesForTenant maps through INTERNAL_TO_OFFICIAL and de-duplicates. */
  /* [2026-10-20] FOUR ADDED on Tiago's instruction — "blackboard: lms, feedback, academic
   * integrity, learning analytics, curriculum, effectivness, ai assistants". Accessibility,
   * Lecture Capture, Analytics & BI, Digital Credentialing and Online Course Marketplaces were on
   * the fit profile but are NOT in that list, so they stay out.
   * institutional_effectiveness_accreditation has 0 live rows (4 closed) — Contract Intelligence
   * only, nothing in Live Radar. */
  blackboard: ['lms', 'Learning Management Systems (LMS)',
               'course_evaluation', 'Feedback & Evaluation',
               'academic_integrity', 'Academic Integrity / Plagiarism Detection',
               'learning_analytics', 'Learning Analytics / Adaptive Insights',
               'curriculum_enrichment_management', 'Curriculum Enrichment & Curriculum Management Platforms',
               'institutional_effectiveness_accreditation', 'Institutional Effectiveness & Accreditation',
               'ai_assistants', 'AI & Virtual Assistants'],
  /* [2026-10-09] terminalfour — higher-ed website CMS, BE lens profile 39
   * (tenant_market_profiles.categories = ['website_development']). 🔴 MUST MIRROR
   * TENANT_DEFAULT_CATEGORIES.terminalfour in api/src/routes/procurementRoutes.ts — when these two
   * drift, the exact-match narrowing in Procurement.jsx silently empties preferredLiveCategories
   * and My Deals renders "(0)" with the tab still visible. Both spellings, because
   * getDealCategoriesForTenant maps through INTERNAL_TO_OFFICIAL and de-duplicates. */
  terminalfour: ['website_development', 'Website Development (Education only)'],
  /* [2026-10-13] lms ADDED (Tiago). constructor_tech is multi-product (PROCTORING / MARKING /
   * ONLINE_EXAMS / LMS) on one lens, profile 34. The BE's TENANT_DEFAULT_CATEGORIES.constructor_tech
   * in api/src/routes/procurementRoutes.ts has carried all four since 2026-10-09 while this list
   * still had two, so the mirror these comments require was already broken before this change.
   * Measured before adding, per the rule above: LMS is the LARGEST of the four, not an empty
   * module — 438 Live deals and 5,176 visible closed deals.
   * ⚠️ STILL DRIFTED: the BE also lists online_exams / 'Online Exam Platforms' (211 Live,
   * 1,645 closed) and this list does not. Left out because only lms was asked for; adding it is
   * the one-line change that would restore the full mirror.
   * Both spellings, because getDealCategoriesForTenant maps through INTERNAL_TO_OFFICIAL and
   * de-duplicates. ⚠️ getCategoriesForTenant is also read by salesMap, chatbotRoutes, answer and
   * analyticsRoutes, so this widens those surfaces for constructor_tech too. */
  constructor_tech: ['proctoring', 'Remote Proctoring',
                     'marking', 'Marking / Grading Platforms',
                     'lms', 'Learning Management Systems (LMS)'],
  // [2026-10-04] efekta: language learning plus AI & virtual assistants, matching the BE's
  // TENANT_DEFAULT_CATEGORIES entry. Kept to TWO on purpose — efekta's Market lens is
  // tenant_market_profiles profile 36 (categories = ['language_learning']), and the BE map is
  // read by SalesMap, the chatbot and analytics as well as procurement, so this list must not
  // widen beyond the lane. The three digital-content categories on efekta's tender product
  // profiles are deliberately absent. 'AI & Virtual Assistants' is written as the official name
  // because INTERNAL_TO_OFFICIAL has no ai_assistants key and getDealCategoriesForTenant passes
  // an unmapped value through unchanged — the pattern demarque already uses above.
  efekta: ['language_learning', 'Language Learning', 'AI & Virtual Assistants'],
  jpik: ['it_hardware', 'IT Hardware'],
  /* [2026-10-10] verypc — VeryPC, the UK education PC/laptop maker. BE lens profile 40
   * (tenant_market_profiles.categories = ['it_hardware']). 🔴 MUST MIRROR
   * TENANT_DEFAULT_CATEGORIES.verypc in api/src/routes/procurementRoutes.ts — when these two drift,
   * the exact-match narrowing in Procurement.jsx silently empties preferredLiveCategories and My
   * Deals renders "(0)" with the tab still visible, which is exactly how the jpik/it_hardware defect
   * presented. The FE's own label is used here ('IT Hardware'); the BE taxonomy's longer
   * 'IT Hardware (Education only)' is bridged by the CATEGORY_VARIANTS alias added for jpik. */
  verypc: ['it_hardware', 'IT Hardware'],
  almentor: ['E-learning Content / Authoring / Learning Services', 'Digital Course Content & Inclusive Access'],
  infonetica: ['Research & Data Management Platforms'],
  cayuse: ['Research & Data Management Platforms'],
  seba: [],
  rumi: ['academic_integrity', 'Academic Integrity / Plagiarism Detection'],
  feedbackfruits: ['Marking / Grading Platforms', 'Classroom Engagement & Polling'],
  /* [2026-10-20] feedbackfruits_V2 = marking, online exams, student success, learning analytics
   * (Tiago). Mirrors the new explicit TENANT_DEFAULT_CATEGORIES.feedbackfruits_V2 in the BE; the
   * tenant previously had no BE key and inherited the base brand's LMS + SIS scope.
   * 🔴 THE LOWERCASE KEY IS THE ONE THAT GETS HIT, and the pre-existing lowercase entry here was
   * right — I wrongly called it a defect. Callers resolve the tenant as `.trim().toLowerCase()`,
   * which is why every key in the BE's TENANT_DEFAULT_CATEGORIES is lowercase too. A capital-V key
   * alone is never reached: the tenant silently falls back to the base `feedbackfruits` brand and
   * keeps its LMS + SIS scope, which is exactly what the live /bootstrap response showed after the
   * first deploy. Both spellings now carry the same list. */
  feedbackfruits_v2: ['marking', 'Marking / Grading Platforms',
                      'online_exams', 'Online Exam Platforms',
                      'student_success', 'Student Success & Engagement',
                      'learning_analytics', 'Learning Analytics / Adaptive Insights',
                      'polling', 'Classroom Engagement & Polling'],
  feedbackfruits_V2: ['marking', 'Marking / Grading Platforms',
                      'online_exams', 'Online Exam Platforms',
                      'student_success', 'Student Success & Engagement',
                      'learning_analytics', 'Learning Analytics / Adaptive Insights',
                      'polling', 'Classroom Engagement & Polling'],
};

/**
 * Vendor name variants → canonical display name for pie chart consolidation.
 * Maps various spellings and product names to a single display label.
 */
const VENDOR_CANONICAL = {
  // Finance & ERP / HCM. [2026-10-15] Benchmarking showed "Unit4" and "Unit4 Business Software Ltd" as two
  // vendors (deals also carry "Unit4 Business Software Netherlands"); containment matching folds them all.
  // Not 'unit 4': matching is substring, and 'unit 4' sits inside 'Community Unit 45 School'.
  'unit4': 'Unit4',
  'agresso': 'Unit4',
  // [2026-10-22] Student financial wellbeing / hardship funding — the blackbullion lens.
  // Checked against all 74 pre-existing keys before adding: no containment collision either way
  // (matching here is bidirectional containment, so a short or polysemous token poisons the set).
  'blackbullion': 'Blackbullion',
  // [2026-10-31] wooclap lens. Coined names. Tested against every supplier string in deals + closed_deals:
  // the only containment hit is a one-letter supplier "e" (1 row), which already matches most keys here.
  'wooclap': 'Wooclap',
  'vevox': 'Vevox',
  // [2026-11-02] polleverywhere lens. Two-word brand; no containment collision with any existing key.
  'poll everywhere': 'Poll Everywhere',
  'polleverywhere': 'Poll Everywhere',
  // [2026-11-03] positivo. Full brand only — bare 'positivo' sits inside 'dispositivos' (bidirectional containment).
  'positivo tecnologia': 'Positivo Tecnologia',
  'paymystudent': 'PayMyStudent',
  'pay my student': 'PayMyStudent',
  /* [2026-10-22] JS Group (John Smith Group), discovered this session and added to
   * tenant_market_profiles.competitor_set for blackbullion. It trades its student-funds platform as
   * Aspire / Aspire Cash and WHITE-LABELS IT PER INSTITUTION (sussex-aspire.co.uk, wlv-wallet.co.uk,
   * aspire.hud.ac.uk, aspire.brighton.ac.uk ...), so the brand a student sees reads as in-house and the
   * only reliable tell is the footer "Copyright (c) 2007 - 2026 JS Group". 25 cells written.
   * 🔴 ONLY 'js group' IS SAFE HERE, and that was measured rather than assumed: matching is
   * bidirectional containment (`key.includes(variant) || variant.includes(key)`), so a SHORT supplier
   * string matches inside a LONG variant. Tested against all 73,018 distinct supplier strings in
   * `deals` + `closed_deals` on 2026-10-22:
   *   'js group'          -> 0 matches. SAFE.
   *   'john smith group'  -> swallows the suppliers "MIT" and "SMI". EXCLUDED.
   *   'aspire cash'       -> swallows the supplier "CAS". EXCLUDED.
   *   'aspire' (bare)     -> 11 unrelated suppliers (ASPIRE INDUSTRIAL SERVICES, AspirEDU, Aspire HR
   *                          Inc, Aspire Technology Partners ...). EXCLUDED — the same unbounded-token
   *                          failure as a bare 'Blackbaud' key. */
  'js group': 'JS Group',
  // 🔴 AcademicWorks is DELIBERATELY NOT FOLDED HERE, and the reason is the matcher: lookup is
  // exact-key first, then a loop whose test is `key.includes(variant) || variant.includes(key)` —
  // containment in BOTH directions, so a SHORT supplier string matches inside a LONG variant.
  // Measured 2026-10-22: a key 'blackbaud academicworks' would therefore swallow the 447
  // `closed_deals` rows whose supplier is exactly "Blackbaud" (alumni_advancement, a different
  // product line) and relabel them AcademicWorks, and a key 'academicworks' would swallow the 2
  // rows whose supplier is exactly "Academic". Folding buys nothing anyway: toCanonicalVendorName
  // falls through to rawName.trim(), so "AcademicWorks" already displays as AcademicWorks. The
  // three estate spellings (AcademicWorks 36 cells, Blackbaud AcademicWorks 4, Blackbaud Award
  // Management (AcademicWorks) 1) stay split in the chart — cosmetic, and strictly better than
  // mislabelling 447 rows. They ARE folded where it is safe to do so: the watchlist note on
  // admin_atoms.competitor_watchlist and incumbent_penalty_signals both carry all three.
  'awardspring': 'AwardSpring',
  'inceptia': 'Inceptia',
  'financial avenue': 'Inceptia',
  'igrad': 'iGrad',
  'edquity': 'Edquity',
  'cashcourse': 'CashCourse',
  // LMS
  'anthology': 'Anthology',
  'blackboard': 'Anthology',
  'blackboard learn': 'Anthology',
  'canvas': 'Canvas',
  'canvas lms': 'Canvas',
  'instructure': 'Canvas',
  'instructure canvas': 'Canvas',
  'instructure canvas lms': 'Canvas',
  'moodle': 'Moodle',
  'open lms': 'Moodle',
  'd2l': 'D2L',
  'brightspace': 'D2L',
  'google classroom': 'Google Classroom',
  'sakai': 'Sakai',
  'schoology': 'Schoology',
  'schoology learning': 'Schoology',
  // Proctoring
  'smowl': 'Smowl',
  'smowltech': 'Smowl',
  'proctorio': 'Proctorio',
  'respondus': 'Respondus',
  'respondus lockdown': 'Respondus',
  'proctoru': 'ProctorU',
  'honorlock': 'Honorlock',
  'inspera': 'Inspera',
  'the invigilator': 'The Invigilator',
  // SIS / CRM
  'ellucian banner': 'Ellucian Banner',
  'ellucian colleague': 'Ellucian Colleague',
  'oracle peoplesoft': 'Oracle PeopleSoft',
  'peoplesoft': 'Oracle PeopleSoft',
  'workday student': 'Workday Student',
  /* [2026-10-15] Vendor level outside SIS. 'workday' -> 'Workday Student' put Workday's finance and HR deals in
   * Benchmarking under a student-system name, and a bare 'Oracle' became 'Oracle PeopleSoft' because matching is
   * substring and 'oracle' sits inside 'oracle peoplesoft'. In the SIS category the product names are restored
   * by SIS_PRODUCT_NAME below. 'oracle' comes AFTER the PeopleSoft keys so 'Oracle PeopleSoft ...' still wins. */
  'workday': 'Workday',
  'oracle': 'Oracle',
  'powerschool': 'PowerSchool',
  'salesforce': 'Salesforce',
  'microsoft dynamics': 'Microsoft Dynamics 365',
  'dynamics 365': 'Microsoft Dynamics 365',
  'anthology crm': 'Anthology CRM',
  // Assessment
  'alek': 'ALEK',
  // ePortfolio
  'pebblepad': 'PebblePad',
  'mahara': 'Mahara',
  'portfolium': 'Portfolium',
  // Library
  'ex libris alma': 'Ex Libris Alma',
  'alma': 'Ex Libris Alma',
  'overdrive': 'Overdrive',
  'oclc worldshare': 'OCLC WorldShare',
  'sirsi dynix': 'SirsiDynix',
  'sirsidynix': 'SirsiDynix',
  'koha': 'Koha',
  'ebsco': 'EBSCO',
  // Survey / feedback
  'electric paper': 'Evasys',
  'evasys': 'Evasys',
  'explorance': 'Explorance',
  'explorance blue': 'Explorance',
  // Exam / assessment
  'examsoft': 'ExamSoft',
  'exam4': 'Exam4',
  'evaexam': 'EvaExam',
  // [2026-10-04] 'inspera' removed here — it is already mapped in the proctoring group above, to
  // the SAME value ('Inspera'), so this was a duplicate key with no behavioural effect: nothing
  // was being silently overwritten. Kept in the proctoring group because Inspera sells both and
  // the first occurrence wins for readers scanning the file. Clears eslint no-dupe-keys.
  'janison': 'Janison',
  'wiseflow': 'WISEflow',
  'uniwise': 'UNIwise',
  'questionmark': 'Questionmark',
  'digiexam': 'Digiexam',
  'cadmus': 'Cadmus',
  'vretta': 'Vretta',
  'synap': 'Synap',
  // Pearson
  'pearson': 'Pearson',
  'pearson vue': 'Pearson',
  'onvue': 'Pearson',
  'ncs pearson': 'Pearson',
  'prometric': 'Pearson',
  // Turnitin
  'turnitin': 'Turnitin',
  // UWorld
  'uworld': 'UWorld',
  'usmleworld': 'UWorld',
};

/** Course evaluation category: keyword → normalized vendor name */
const COURSE_EVALUATION_VENDOR_CANONICAL = {
  'qualtrics': 'Qualtrics',
  'evasys': 'Evasys',
  'electric paper': 'Evasys',
  'scantron': 'Evasys',
  'explorance': 'Explorance',
  'watermark': 'Watermark',
  'evaluationkit': 'Watermark',
  'anthology': 'Anthology',
  'campus labs': 'Anthology',
  'idea': 'Anthology',
  'smartevals': 'SmartEvals',
  'collegenet': 'CollegeNet',
  'carahsoft': 'Carahsoft',
  'drieam': 'Drieam',
  'heliocampus': 'HelioCampus',
  'jisc': 'Jisc',
  'limesurvey': 'LimeSurvey',
  'lime survey': 'LimeSurvey',
  'snap survey': 'Snap Surveys',
  'snap surveys': 'Snap Surveys',
  'smartsurvey': 'SmartSurvey',
  'ipsos': 'Ipsos',
  'taskstream': 'Taskstream',
  'omnivox': 'Omnivox',
  'one45': 'One45',
  'medhub': 'MedHub',
  'feedbackfruits': 'FeedbackFruits',
  'berger-levrault': 'Berger-Levrault',
  'berger levrault': 'Berger-Levrault',
};

function isCourseEvaluationCategory(category) {
  if (!category || typeof category !== 'string') return false;
  const k = String(category).trim().toLowerCase().replace(/[\s-]+/g, '_');
  return k.includes('course_evaluation') || k.includes('course_eval') || (k.includes('course') && k.includes('evaluation'));
}

/** Check if supplier/product_name indicates internal/proprietary (case-insensitive). */
const INTERNAL_KEYWORDS = ['internal', 'proprietary', 'institution-hosted', 'custom', 'homegrown'];

export function isInternalOrProprietary(value) {
  if (!value || typeof value !== 'string') return false;
  const v = value.trim().toLowerCase();
  if (INTERNAL_KEYWORDS.some((kw) => v.includes(kw))) return true;
  if (v.includes('university') && v.includes('survey')) return true;
  if (v.includes('university') && v.includes('questionnaire')) return true;
  return false;
}

/** Collapse legal-entity suffixes so passthrough wedges bucket by recognizable name. */
function cleanPassthroughSupplier(raw) {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return '';
  const cleaned = trimmed
    .replace(/\s+/g, ' ')
    .replace(/\b(ltd|llc|inc|gmbh|sa|sas|pty|plc|limited|incorporated)\b\.?/gi, '')
    .replace(/[,;:]\s*$/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return cleaned || trimmed;
}

/** Known-competitor map lookup; null when no canonical match (caller may passthrough). */
function matchCanonicalVendor(rawName, category) {
  if (!rawName || typeof rawName !== 'string') return null;
  const key = rawName.trim().toLowerCase();
  if (!key) return null;
  if (INTERNAL_KEYWORDS.some((kw) => key.includes(kw))) return 'Internal';
  if (key.includes('university') && (key.includes('survey') || key.includes('questionnaire'))) {
    return 'Internal';
  }
  if (key.includes('lms') && (key.includes('built in') || key.includes('built-in'))) {
    return 'Built-in LMS';
  }
  const map = isCourseEvaluationCategory(category) ? COURSE_EVALUATION_VENDOR_CANONICAL : null;
  if (map) {
    if (map[key]) return map[key];
    for (const [variant, canonical] of Object.entries(map)) {
      if (key.includes(variant) || variant.includes(key)) return canonical;
    }
  }
  if (VENDOR_CANONICAL[key]) return VENDOR_CANONICAL[key];
  for (const [variant, canonical] of Object.entries(VENDOR_CANONICAL)) {
    if (key.includes(variant) || variant.includes(key)) return canonical;
  }
  return null;
}

/**
 * Market share / closed-deal attribution: current supplier only (not incumbent_vendor).
 * Returns null when supplier is empty so consumers reserve "Unknown" for that case.
 */
export function toCanonicalVendorNameFromDeal(deal, tenantId) {
  const supplier = String(deal?.supplier ?? '').trim();
  if (!supplier) return null;

  const productName = String(deal?.product_name ?? '').trim();
  if (isInternalOrProprietary(supplier) || isInternalOrProprietary(productName)) return 'Internal';

  const tenantNorm = normalizeVendorForDeal(supplier, tenantId);
  const normalized = String(tenantNorm ?? '').trim();
  if (!normalized) return null;

  const canonical = matchCanonicalVendor(normalized, deal?.category);
  if (canonical) return canonical;

  return cleanPassthroughSupplier(normalized);
}

/** Chart caption only. LMS-native cells share one slice; the stored vendor name is unchanged. */
export function readVendorState(record) {
  const value = record?.vendor_state
    ?? record?.incumbent_state
    ?? record?.incumbent?.vendor_state
    ?? record?.account?.vendor_state
    ?? record?.account?.incumbent_state
    ?? null;
  const state = String(value ?? '').trim().toLowerCase();
  return state || null;
}

export function marketShareVendorLabel(deal, tenantId, options = {}) {
  if (readVendorState(deal) === 'lms_native') return 'Built-in LMS';
  const raw = rawShareVendorName(deal);
  if (isBuiltInLmsPlaceholder(raw)) return 'Built-in LMS';
  if (options.collapseLmsFamily && isExactLmsFamilyName(raw)) return 'Built-in LMS';
  const bundled = bundledNonLmsVendor(raw);
  if (bundled) return bundled;
  const canonical = toCanonicalVendorNameFromDeal(deal, tenantId) || raw || 'Unknown';
  if (options.collapseLmsFamily && isExactLmsFamilyName(canonical)) return 'Built-in LMS';
  return canonical;
}

/** True when the selected Contract Intelligence categories include LMS. */
export function lensIncludesLmsCategory(categories = []) {
  return (categories || []).some((c) => toCategoryKeyForApi(c) === 'lms');
}

/** Name collapse is safe only in a non-empty lens that does not include LMS. */
export function shouldCollapseLmsFamily(categories = []) {
  return (categories || []).length > 0 && !lensIncludesLmsCategory(categories);
}

const LMS_FAMILY_NAMES = [
  'canvas',
  'canvas lms',
  'instructure',
  'instructure canvas',
  'instructure canvas lms',
  'canvas quizzes',
  'canvas quizzes / online assessments',
  'canvas timed online assignments (toa)',
  'blackboard',
  'blackboard learn',
  'anthology',
  'anthology blackboard learn ultra',
  'd2l',
  'd2l brightspace',
  'brightspace',
  'brightspace quizzes',
  'moodle',
  'moodle community',
  'open lms',
  'sakai',
  'schoology',
  'schoology learning',
  'google classroom',
  'classter',
];

const LMS_FAMILY_KEYS = new Set([
  ...LMS_FAMILY_NAMES,
  'lms built-in',
  'built-in lms',
  'lms built in',
  'built in lms',
]);

const LMS_TOKEN_KEYS = [
  'anthology blackboard learn ultra',
  'instructure canvas lms',
  'instructure canvas',
  'canvas lms',
  'canvas quizzes / online assessments',
  'canvas timed online assignments (toa)',
  'canvas quizzes',
  'brightspace quizzes',
  'd2l brightspace',
  'blackboard learn',
  'google classroom',
  'schoology learning',
  'moodle community',
  'open lms',
  'anthology',
  'blackboard',
  'brightspace',
  'instructure',
  'schoology',
  'classter',
  'canvas',
  'moodle',
  'sakai',
  'd2l',
];

function normalizeShareName(value) {
  return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function rawShareVendorName(deal) {
  return String(deal?.supplier ?? deal?.vendor ?? deal?.vendor_name ?? '').trim();
}

function isBuiltInLmsPlaceholder(raw) {
  const key = normalizeShareName(raw);
  return key === 'lms built-in' || key === 'built-in lms' || key === 'lms built in' || key === 'built in lms';
}

function isExactLmsFamilyName(raw) {
  return LMS_FAMILY_KEYS.has(normalizeShareName(raw));
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function wholeWordIndex(haystack, needle) {
  const re = new RegExp(`(?:^|[^a-z0-9])${escapeRegExp(needle)}(?:$|[^a-z0-9])`, 'i');
  const match = re.exec(haystack);
  return match ? match.index : -1;
}

function containsWholeWord(haystack, needle) {
  return wholeWordIndex(haystack, needle) >= 0;
}

/** A bundled name like "Respondus … + D2L Brightspace" stays the non-LMS product. */
function bundledNonLmsVendor(raw) {
  const key = normalizeShareName(raw);
  if (!key || isExactLmsFamilyName(raw)) return null;
  if (!LMS_TOKEN_KEYS.some((token) => containsWholeWord(key, token))) return null;
  let best = null;
  for (const [variant, canonical] of Object.entries(VENDOR_CANONICAL)) {
    if (LMS_FAMILY_KEYS.has(variant)) continue;
    const idx = wholeWordIndex(key, variant);
    if (idx < 0) continue;
    if (!best || idx < best.idx || (idx === best.idx && variant.length > best.len)) {
      best = { canonical, idx, len: variant.length };
    }
  }
  return best?.canonical || null;
}

function normalizeVendorForDeal(name, tenantId) {
  if (!name) return '';
  const own = TENANT_OWN_PRODUCTS[(tenantId || '').toLowerCase().trim()] || [];
  const n = String(name).trim().toLowerCase();
  for (const p of own) {
    if (p.toLowerCase() === n || n.includes(p.toLowerCase()) || p.toLowerCase().includes(n)) {
      return own[0] || p;
    }
  }
  return name;
}

const SIS_PRODUCT_NAME = { Workday: 'Workday Student', Oracle: 'Oracle PeopleSoft' };
const isSisCategory = (category) => /\bsis\b|student information/i.test(String(category || ''));

export function toCanonicalVendorName(rawName, category) {
  let matched = matchCanonicalVendor(rawName, category);
  if (matched && isSisCategory(category) && SIS_PRODUCT_NAME[matched]) matched = SIS_PRODUCT_NAME[matched];
  if (matched) return matched;
  if (!rawName || typeof rawName !== 'string') return 'Unknown';
  const key = rawName.trim().toLowerCase();
  if (!key) return 'Unknown';
  return rawName.trim();
}

/** Tenant → own product names/variants (excluded from competitor list, used for pie chart tenant clients) */
export const TENANT_OWN_PRODUCTS = {
  instructure: ['Canvas', 'Instructure', 'Canvas LMS'],
  smowl: ['Smowl', 'Smowltech', 'SMOWL'],
  surpass: ['Surpass', 'Surpass Assessment Systems'],
  surpass_v2: ['Surpass', 'Surpass Assessment Systems'],
  vretta: ['Vretta'],
  diambra: ['Diambra'],
  demarque: ['De Marque', 'Demarque', 'DeMarque'],
  demarque_v2: ['De Marque', 'Demarque', 'DeMarque'],
  bibliotheca: ['Bibliotheca'],
  skillwell: ['Skillwell', 'Skillwise', 'etu', 'ETU'],
  kairadigital: ['Kaira', 'Kaira Digital'],
  wayground: ['Wayground'],
  plataformaa: ['Plataformaa', 'Plataforma A'],
  calmedica: ['Calmedica'],
  faria: ['Faria', 'OpenApply', 'Schoolsbuddy'],
  edqwest: ['Edqwest'],
  clairelabs: ['Claire Labs', 'ClaireLabs'],
  prowise: ['Prowise'],
  sebit: ['Sebit'],
  base44: ['Base44'],
  anthology: ['Anthology', 'Blackboard'],
  moodle: ['Moodle', 'Open LMS'],
  d2l: ['D2L', 'Brightspace'],
  funidata: ['Funidata'],
  akari: ['Akari', 'Akari Software'],
  gravyty: ['Gravyty'],
  uplanner: ['uPlanner', 'UPlanner'],
  edusign: ['Edusign', 'EduSign'],
  liaison: ['Liaison', 'Liaison International', 'Liaison Edu', 'LiaisonEDU'],
  fullfabric: ['Full Fabric', 'FullFabric'],
  fullfabric_v2: ['Full Fabric', 'FullFabric'],
  studentcrm: ['StudentCRM', 'Student CRM', 'Student-CRM'],
  unit4: ['Unit4', 'Unit 4'],
  // Own-product strings only, and qualified ones: bare 'Access' matches SensusAccess and
  // Level Access, bare 'Dimensions' matches ClickDimensions. Mirrors OWN_PRODUCT_ALIASES
  // .access_people in api/src/utils/tenantFamily.ts.
  access_people: ['The Access Group', 'Access UK', 'Access PeopleHR', 'Access Dimensions',
                  'CoreHR', 'Core Portal', 'PeopleXD', 'Paycircle', 'Eploy'],
  // Own-product strings only. 'Ally' alone is omitted: 'Learning Ally' is an unrelated
  // organisation and matching here is containment.
  blackboard: ['Blackboard', 'Blackboard Learn', 'Blackboard Ultra', 'Blackboard Ally'],
  constructor_tech: ['Constructor'],
  // Mirrors OWN_PRODUCT_ALIASES.testwe in api/src/utils/tenantFamily.ts.
  testwe: ['TestWe', 'Test We', 'ProctorWe'],
  // [2026-10-22] Mirrors OWN_PRODUCT_ALIASES.blackbullion in api/src/utils/tenantFamily.ts.
  // Spellings observed in the estate's 9 inst_vendor_stack cells. 🔴 No short token: matching is
  // containment, and 'Blackboard' and 'Blackbaud' are different companies with their own accounts.
  blackbullion: ['Blackbullion', 'BlackBullion', 'Black Bullion'],
  // [2026-10-31] Mirrors OWN_PRODUCT_ALIASES.wooclap in api/src/utils/tenantFamily.ts. Vevox was acquired by
  // Wooclap on 2026-09-15 and Auga Technologies is the company that made it, so both are the install base.
  wooclap: ['Wooclap', 'Wooflash', 'Vevox', 'Auga Technologies'],
  // [2026-11-02] Mirrors OWN_PRODUCT_ALIASES.polleverywhere in api/src/utils/tenantFamily.ts.
  polleverywhere: ['Poll Everywhere', 'PollEverywhere', 'Survey Everywhere'],
  // [2026-11-03] Mirrors OWN_PRODUCT_ALIASES.positivo in api/src/utils/tenantFamily.ts (never bare 'Positivo').
  positivo: ['Positivo Tecnologia', 'Positivo Informática', 'Positivo Tecologia'],
  efekta: ['Efekta'],
  jpik: ['JPIK', 'Jpik'],
  almentor: ['Almentor'],
  cayuse: ['Cayuse', 'Cayuse SP', 'Cayuse Sponsored Projects'],
  synap: ['Synap'],
  scantron: ['Scantron'],
  seba: ['Seba'],
  rumi: ['Rumi'],
  feedbackfruits: ['FeedbackFruits'],
  feedbackfruits_v2: ['FeedbackFruits'],
};

/**
 * Get the list of competitor vendor names for a tenant.
 * Returns vendors in the tenant's categories, excluding the tenant's own products.
 * Falls back to all vendors from tenant's categories if tenant not in TENANT_OWN_PRODUCTS.
 */
export function getCompetitorsForTenant(tenantId) {
  const tid = (tenantId || '').toLowerCase().trim();
  const categories = TENANT_CATEGORIES[tid] || TENANT_CATEGORIES.instructure;
  const ownProducts = TENANT_OWN_PRODUCTS[tid] || [];

  const allVendors = new Set();
  categories.forEach((cat) => {
    const vendors = CATEGORY_VENDORS[cat] || [];
    vendors.forEach((v) => allVendors.add(v));
  });

  const ownLower = new Set(ownProducts.map((p) => p.toLowerCase().trim()));
  const competitors = Array.from(allVendors).filter((v) => {
    const vLower = v.toLowerCase().trim();
    return !ownLower.has(vLower) && !ownLower.has(vLower.replace(/\s+/g, ''));
  });

  return competitors.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

/**
 * Check if a vendor name matches any in a list (case-insensitive, partial).
 */
export function vendorMatches(vendorName, competitorList) {
  if (!vendorName || !competitorList?.length) return false;
  const v = String(vendorName).trim().toLowerCase();
  return competitorList.some((c) => {
    const cLower = c.trim().toLowerCase();
    return v.includes(cLower) || cLower.includes(v);
  });
}

/** True when tenant has configured procurement categories (empty array = none). */
export function tenantHasProcurementCategories(tenantId) {
  const tid = (tenantId || '').toLowerCase().trim();
  if (Object.prototype.hasOwnProperty.call(TENANT_CATEGORIES, tid)) {
    return (TENANT_CATEGORIES[tid]?.length ?? 0) > 0;
  }
  return true;
}

/** Get categories for a tenant (from TENANT_CATEGORIES). */
export function getCategoriesForTenant(tenantId) {
  const tid = (tenantId || '').toLowerCase().trim();
  return TENANT_CATEGORIES[tid] || TENANT_CATEGORIES.instructure;
}

/**
 * Get official category names for a tenant (for default filter in Competitor Intelligence).
 *
 * [2026-10-04] De-duplicated. Several tenants are listed BOTH as the internal key and as the
 * official label — deliberately, because INTERNAL_TO_OFFICIAL has no entry for some keys and an
 * unmapped value passes through unchanged. Where a key DOES map, both forms collapse to the same
 * label and the result carried it twice: efekta, jpik and rumi (3 of 8 tenants checked) each
 * returned a duplicate. Nothing filtered wrongly — every consumer tests membership or builds a Set
 * — but the selection drives the "(n)" category badge, so Contract Intelligence showed "(3)" for
 * efekta's two real categories. Collapsing here fixes the badge for every caller at once.
 */
export function getDealCategoriesForTenant(tenantId) {
  const cats = TENANT_CATEGORIES[(tenantId || '').toLowerCase().trim()] || TENANT_CATEGORIES.instructure;
  return [...new Set(cats.map((c) => INTERNAL_TO_OFFICIAL[c] || c).filter(Boolean))];
}

/** Map official category names back to internal codes (for getVendorsForCategories). */
export function dealStringsToInternalCategories(officialNames) {
  if (!officialNames?.length) return [];
  const officialToInternal = {};
  Object.entries(INTERNAL_TO_OFFICIAL).forEach(([code, official]) => {
    if (official) officialToInternal[official] = code;
  });
  const set = new Set();
  officialNames.forEach((s) => {
    const code = officialToInternal[s];
    if (code) set.add(code);
    else set.add(s);
  });
  return Array.from(set);
}

/**
 * Get vendors for given categories.
 * By default excludes tenant's own products; set includeOwnProducts=true to include them (e.g. for Vendor filter).
 */
export function getVendorsForCategories(categories, tenantId, includeOwnProducts = false) {
  if (!categories?.length) return [];
  const ownProducts = includeOwnProducts ? [] : (TENANT_OWN_PRODUCTS[(tenantId || '').toLowerCase().trim()] || []);
  const ownLower = new Set(ownProducts.map((p) => p.toLowerCase().trim()));
  const allVendors = new Set();
  categories.forEach((cat) => {
    const vendors = CATEGORY_VENDORS[cat] || [];
    vendors.forEach((v) => allVendors.add(v));
  });
  const filtered = Array.from(allVendors).filter((v) => {
    const vLower = v.toLowerCase().trim();
    return !ownLower.has(vLower) && !ownLower.has(vLower.replace(/\s+/g, ''));
  });
  if (!filtered.includes('Internal')) filtered.push('Internal');
  return filtered.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}
